<template>
  <div class="tree-item" :class="{ editable }" :data-id="item.id">
    <div
      ref="itemName"
      class="v-centerer tree-item__name"
      :style="nameStyle"
      @click="clicked()"
    >
      <Icon class="tree-item__expander" :style="arrowStyle">
        {{ arrowDownLine }}
      </Icon>
      <Checkbox
        v-if="!editable"
        :value="isItemSelected"
        stop-propagation
        @input="itemSelected"
      />
      <EditableLabel
        ref="name"
        v-model="item[nameKey]"
        disabled
        @input="$emit('nameEdited', item)"
      />
      <span v-if="editable" class="options">
        <img
          class="edit-img"
          src="/images/edit.png"
          alt=""
          @click.stop="$refs.name.edit()"
        >
        <img
          class="edit-img"
          src="/images/delete.png"
          alt=""
          @click.stop="deleteItem"
        >
      </span>
    </div>
    <!-- Must be a v-show for findTreeItemComponent to work -->
    <div v-show="isItemOpened" class="sub-items">
      <TreeItem
        v-for="subitem in item[childrenKey]"
        ref="children"
        :key="subitem.id"
        :item="subitem"
        :editable="editable"
        :level="Number(level) + 1"
        :children-key="childrenKey"
        :name-key="nameKey"
        :add-btn-text="addBtnText"
        :tree-opened-values="treeOpenedValues"
        :tree-values="treeValues"
        @nameEdited="itemEdited"
        @parentChange="parentChanged"
        @addItem="addItem"
        @deleteItem="deleteSubItem"
        @itemSelected="itemSelected"
        @open="openSubitem"
      />
      <button
        v-if="editable"
        class="add-button italic"
        :style="btnStyle"
        @click="$emit('addItem', item)"
      >
        <img src="/images/add.png" alt="add">
        {{ addBtnText }}
      </button>
      <div class="tree-level" :style="treeLevelStyle" />
    </div>
  </div>
</template>

<script>
/**
 * Component displayed in a tree of objects.
 *
 * @vue-prop {Object} item - Value displayed in the item (Zone or Type object)
 * @vue-prop {Boolean} [editable=false] - Whether the object should be editable
 * @vue-prop {Number} [level=1] - Level inside the tree
 * @vue-prop {String} [childrenKey=zones] - Key of the array of children in `item`
 * @vue-prop {String} [nameKey=name] - Key of the displayed name of the item
 * @vue-prop {String} [addBtnText] - Text to display in the button used to add a sub-item
 * @vue-prop {Array} treeValues - Value of the parent tree to know if the item is selected
 * @vue-prop {Obejct} treeOpenedValues - Value of the parent tree to know if the item is opened
 *
 */

import $ from 'jquery';
// Components
import Checkbox from '@/components/checkbox.vue';
import EditableLabel from '@/components/editable-label.vue';
import Icon from '@/components/icon.vue';
// Assets & utils
import { treeAsArray } from '@/utils/main';
import { arrowDownLine } from '@/assets/icons';

export default {

  name: 'TreeItem',

  components: {
    Checkbox,
    EditableLabel,
    Icon,
  },

  props: {
    item: {
      type: Object,
      required: true,
    },
    editable: Boolean,
    level: {
      type: Number,
      default: 1,
    },
    childrenKey: {
      type: String,
      default: 'zones',
    },
    nameKey: {
      type: String,
      default: 'name',
    },
    addBtnText: {
      type: String,
      default() {
        return this.$t('message.ADD_A_LOCATION');
      },
    },
    treeValues: {
      type: Array,
      default: () => [],
    },
    treeOpenedValues: {
      type: Object,
      default: () => ({}),
    },
  },

  data() {
    return {
      arrowDownLine,
    };
  },

  computed: {
    /** Object: Style applied to the '.name' element to increase padding each level */
    nameStyle() {
      return { paddingLeft: `${this.level * 20}px` };
    },
    btnStyle() {
      return { paddingLeft: `${(this.level + 1) * 20}px` };
    },
    arrowStyle() {
      const { item, childrenKey } = this;
      return {
        transform: this.isItemOpened ? 'rotate(0deg)' : 'rotate(-90deg)',
        visibility: this.editable || (item[childrenKey] && item[childrenKey].length) ? 'visible' : 'hidden',
      };
    },
    treeLevelStyle() {
      return { left: `${this.level * 20 + 4}px` };
    },
    /** Array: Array of ids of all the children of the curren item */
    allChildrenIds() {
      return treeAsArray(this.item[this.childrenKey], this.childrenKey).map(item => item.id);
    },
    /** Boolean: Should the current item be opened */
    isItemOpened() {
      // The item must be opened if it was manually opened (treeOpenedValues prop)
      // Or if it contains a selected item
      if (this.treeOpenedValues[`${this.item.id}`] === undefined) {
        return this.allChildrenIds.some(id => this.treeValues.includes(id));
      }
      return this.treeOpenedValues[`${this.item.id}`];
    },
    /** Boolean: Is the current item selected */
    isItemSelected() {
      return this.treeValues.includes(this.item.id);
    },
  },

  mounted() {
    this.setupDraggable();
    this.$nextTick(() => {
      // If the item was opened automatically, keep it 'manually' opened
      if (this.isItemOpened && !this.treeOpenedValues[`${this.item.id}`]) {
        this.$emit('open', this.item, true);
      }
    });
  },

  beforeDestroy() {
    const instance = $(this.$el).draggable('instance');
    if (instance) {
      $(this.$el).draggable('destroy');
      $(this.$refs.itemName).droppable('destroy');
    }
  },

  methods: {
    /** Setup the draggable behavior of the item */
    setupDraggable() {
      if (this.editable) {
        $(this.$el).draggable({
          revert: 'invalid',
        });

        $(this.$refs.itemName).droppable({
          accept: '.tree-item',
          tolerance: 'pointer',
          drop: (e, ui) => {
            if (ui.draggable && ui.draggable[0] && ui.draggable[0].dataset) {
              const id = Number(ui.draggable[0].dataset.id);
              if (id) {
                this.$emit('parentChange', { id, parent: this.item.id });
              }
            }
          },
        });
      }
    },
    /** Open the item on click */
    clicked() {
      this.$emit('open', this.item, !this.isItemOpened);
    },
    /** Propagate opening an item */
    openSubitem(item, isOpened) {
      this.$emit('open', item, isOpened);
    },
    /** Sub-item or this.item has been (un)selected */
    itemSelected(subitem, val) {
      if (typeof subitem === 'object') {
        // Child edited, forward event
        this.$emit('itemSelected', subitem, val);
      } else {
        // This item edited, create event, with item first, checkbox value next
        this.$emit('itemSelected', this.item, subitem);
      }
    },
    /** Sub-item has a new child */
    parentChanged(params) {
      this.$emit('parentChange', params);
    },
    /** Sub-item has been edited */
    itemEdited(subitem) {
      this.$emit('nameEdited', subitem);
    },
    /** Add an item to this or to a sub-item */
    addItem(subitem) {
      this.$emit('addItem', subitem);
    },
    /** Delete button clicked for current item */
    deleteItem() {
      this.$emit('deleteItem', this.item);
    },
    /** Sub-item's delete utton clicked */
    deleteSubItem(item) {
      this.$emit('deleteItem', item);
    },
    /** Trigger the edition of the name label */
    startEdit() {
      this.$refs.name.edit();
    },
    unselect() {
      this.item.selected = false;
    },
  },
};
</script>

<style lang="scss">
@import "@/assets/scss/vars.scss";

.tree-item {
  position: relative;
  text-align: left;
  cursor: pointer;

  &.ui-draggable-dragging {
    opacity: 0.7;
    z-index: 1;
  }

  &__name {

    &.ui-droppable-hover {
      background-color: $color-droppable-active;
    }

    &:hover {
      background-color: $main-hover-color;
    }
  }

  &.editable > .tree-item__name {
    padding: 0px;
  }

  &__expander {
    width: 10px;
    min-width: 10px;
    margin-right: 10px;
    transition: transform 0.2s ease-in-out;
  }

  .checkbox {
    padding: 10px;

    &:hover {
      background-color: rgba(0, 0, 0, 0.4);
    }
    .tick {
      margin: 0px;
    }
  }

  .options {
    margin-left: 20px;

    .edit-img {
      width: 20px;
      height: 20px;
      padding: 5px;
    }
  }

  .sub-items {
    position: relative;

    .tree-level {
      position: absolute;
      top: 0px;
      bottom: 0px;
      width: 2px;
      background: rgba(0, 0, 0, 0.1);
    }
  }

  .editable-label {
    height: 1.5em;
    max-width: calc(100% - 65px); // tick = 45px, arrow = 20px
    display: flex;
    align-items: center;

    .editable-label-text {
      text-overflow: ellipsis;
      white-space: nowrap;
      overflow: hidden;
    }
    div.textarea {
      min-width: 50px;
    }
  }
}

@media (max-width: $smartphone-landscape) {
  .tree-item {
    & &__name {
      padding: 3px 0px;
    }
    .checkbox {
      padding: 8px;

      .tick {
        width: 13px;
        height: 13px;
      }
    }
  }
}
</style>
